/**
 * @jsxRuntime classic
 * @jsx jsx
 */
/* eslint-disable check-i18n-id-prefix/i18n-id-prefix-with-package-name-FormattedMessage */
import { Fragment, type FC, useEffect, useCallback, useMemo, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl-next';

import AkShortcutIcon from '@atlaskit/icon/core/link-external';
import { ButtonItem, LinkItem, MenuGroup, Section } from '@atlaskit/menu';
// eslint-disable-next-line no-unused-vars
import { cssMap, jsx } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';

import { QueryInterop as Query } from '@confluence/apollo-relay-interop/entry-points/QueryInterop';
import { ShortcutVisualizer } from '@confluence/shortcuts/entry-points/ShortcutVisualizer';
import { isAtlassianStaff } from '@confluence/navigation-search-dialog/entry-points/isAtlassianStaff';
import { UserContextQuery } from '@confluence/navigation-search-dialog/entry-points/UserContextQuery.graphql';
import type { UserContextQueryResults } from '@confluence/navigation-search-dialog/entry-points/UserContextQuery';
import { LoadableLazy } from '@confluence/loadable';
import { useBlueprintType } from '@confluence/editor-features/entry-points/useBlueprintType';
import { expValEquals } from '@confluence/feature-experiments';

import { isDescendant } from './helpers';
import {
	EDITOR_IMPROVEMENTS_URL,
	EDITOR_TEMPLATES_URL,
	CONTEXTUAL_EDITOR_HELP_LINKS,
	PREDEFINED_BLUEPRINT_TYPES_TITLES,
} from './constants';
import { HelpMenuHelpCenterItem } from './HelpMenuHelpCenterItem';

const styles = cssMap({
	base: {
		backgroundColor: token('elevation.surface.overlay'),
		boxShadow: token('elevation.shadow.overlay'),
		borderRadius: token('radius.small'),
		paddingBlock: token('space.075'),
		marginBlock: token('space.100'),
		marginInline: 'auto',
		maxWidth: '320px',
	},
	shortcut: {
		color: token('color.text.subtlest'),
	},
});

export type HelpMenuProps = {
	openHelpDialog: () => void;
	contentId: string;
	isNCSEditor?: boolean;
	isTemplateEditor?: boolean;
	isCompanyHub?: boolean;
	isOpen?: boolean;
	setIsOpen?: (isOpen: boolean) => void;
	setShouldShowDevTools?: (shouldShowDevTools: boolean) => void;
	setShouldShowGlobalFeedbackCollector?: (shouldShowGlobalFeedbackCollector: boolean) => void;
	triggerRef?: React.RefObject<HTMLButtonElement>;
};

export const HelpMenuComponent: FC<HelpMenuProps> = ({
	openHelpDialog,
	contentId,
	isNCSEditor,
	isTemplateEditor,
	isCompanyHub,
	isOpen,
	setIsOpen,
	setShouldShowDevTools,
	setShouldShowGlobalFeedbackCollector,
	triggerRef,
}) => {
	const containerRef = useRef<HTMLDivElement | null>(null);
	const { formatMessage } = useIntl();
	const blueprintType = useBlueprintType(contentId, isTemplateEditor);

	const handleCloseHelpMenu = useCallback(() => {
		setIsOpen?.(false);
	}, [setIsOpen]);

	// Callback ref to focus the menu container when it mounts for a11y
	const setContainerRef = useCallback((node: HTMLDivElement | null) => {
		containerRef.current = node;
		if (node && expValEquals('confluence_editor_help_menu_keyboard_a11y', 'isEnabled', true)) {
			// Focus the menu container directly - it has tabIndex={-1} to receive programmatic focus
			node.focus();
		}
	}, []);

	// Arrow key navigation for keyboard a11y
	// Implemented manually here because this is not yet supported by the AK Menu component
	// and the DropdownMenu is avoided for performance reasons
	const handleKeyDown = useCallback(
		(event: React.KeyboardEvent) => {
			if (
				!containerRef.current ||
				!expValEquals('confluence_editor_help_menu_keyboard_a11y', 'isEnabled', true)
			) {
				return;
			}

			const items = containerRef.current.querySelectorAll<HTMLElement>(
				'button, a, [role="menuitem"]',
			);
			if (!items.length) {
				return;
			}

			// Check if focus is on the container itself (not on a menu item)
			const focusOnContainer = document.activeElement === containerRef.current;
			const currentIndex = focusOnContainer
				? -1
				: Array.from(items).findIndex((item) => item === document.activeElement);

			switch (event.key) {
				case 'ArrowDown':
					event.preventDefault();
					if (focusOnContainer || currentIndex === -1) {
						// Focus first item when pressing down from container
						items[0].focus();
					} else {
						const nextIndex = currentIndex < items.length - 1 ? currentIndex + 1 : 0;
						items[nextIndex].focus();
					}
					break;
				case 'ArrowUp':
					event.preventDefault();
					if (focusOnContainer || currentIndex === -1) {
						// Focus last item when pressing up from container
						items[items.length - 1].focus();
					} else {
						const prevIndex = currentIndex > 0 ? currentIndex - 1 : items.length - 1;
						items[prevIndex].focus();
					}
					break;
				case 'Escape':
					event.preventDefault();
					handleCloseHelpMenu();
					// Return focus to trigger button
					triggerRef?.current?.focus();
					break;
			}
		},
		[containerRef, handleCloseHelpMenu, triggerRef],
	);

	const handleClickOutside = useCallback(
		(event) => {
			const target = event.target;
			const targetIsNotUnmountedByReact = target.offsetParent;
			if (isOpen && targetIsNotUnmountedByReact && !isDescendant(containerRef.current, target)) {
				handleCloseHelpMenu();
			}
		},
		[isOpen, handleCloseHelpMenu],
	);

	useEffect(() => {
		window.document.addEventListener('click', handleClickOutside);
		return () => {
			window.document.removeEventListener('click', handleClickOutside);
		};
	}, [handleClickOutside]);

	const onItemClick = useCallback(
		(_event, callback) => {
			if (callback) {
				return () => {
					handleCloseHelpMenu();
					callback();
				};
			}
			handleCloseHelpMenu();
		},
		[handleCloseHelpMenu],
	);

	const renderHelpShortcutDropDownItem = useMemo(
		() => (
			<ButtonItem
				onClick={onItemClick(null, openHelpDialog)}
				iconAfter={<ShortcutVisualizer shortcut="mod+/" />}
			>
				<FormattedMessage id="editor.shortcuts" />
			</ButtonItem>
		),
		[onItemClick, openHelpDialog],
	);

	const renderHelpCenter = useMemo(() => {
		return <HelpMenuHelpCenterItem closeHelpMenu={handleCloseHelpMenu} />;
	}, [handleCloseHelpMenu]);

	const renderBlueprintTypeBasedDropdownItems = useMemo(() => {
		if (CONTEXTUAL_EDITOR_HELP_LINKS.hasOwnProperty(blueprintType)) {
			const i18nMessage = {
				id: PREDEFINED_BLUEPRINT_TYPES_TITLES[blueprintType],
			};
			const blueprintTitle = formatMessage(i18nMessage);
			return CONTEXTUAL_EDITOR_HELP_LINKS[blueprintType].map((item, index) => (
				<LinkItem
					key={index}
					onClick={(e) => onItemClick(e, null)}
					href={item.href}
					target="_blank"
				>
					<FormattedMessage
						id="editor.help.how.to.use.blueprint"
						values={{
							blueprintType: blueprintTitle,
						}}
					/>
				</LinkItem>
			));
		}
		return null;
	}, [blueprintType, formatMessage, onItemClick]);

	const renderDevTools = useMemo(() => {
		return (
			<Query<UserContextQueryResults>
				/* @ts-expect-error -- http://go/cfe-use-relay-artifacts */
				query={UserContextQuery}
			>
				{({ data }) => {
					const atlassianStaff = isAtlassianStaff(data);
					return atlassianStaff ? (
						<ButtonItem
							onClick={() => {
								handleCloseHelpMenu();
								setShouldShowDevTools?.(true);
							}}
						>
							<FormattedMessage
								id="editor.help.dev-tools"
								defaultMessage="Dev Tools"
								description="Menu item for internal developer tools"
							/>
						</ButtonItem>
					) : null;
				}}
			</Query>
		);
	}, [handleCloseHelpMenu, setShouldShowDevTools]);

	const renderExternalLinkItems = useMemo(() => {
		if (isTemplateEditor) {
			return null;
		}

		return (
			<Fragment>
				<LinkItem
					href={EDITOR_IMPROVEMENTS_URL}
					target="_blank"
					iconAfter={
						<AkShortcutIcon color="currentColor" label={formatMessage({ id: 'help.name' })} />
					}
				>
					<FormattedMessage id="editor.help.updates" />
				</LinkItem>
				{!isCompanyHub ? (
					<LinkItem
						href={EDITOR_TEMPLATES_URL}
						target="_blank"
						iconAfter={
							<AkShortcutIcon color="currentColor" label={formatMessage({ id: 'help.name' })} />
						}
					>
						<FormattedMessage id="editor.help.templates" defaultMessage="Use page templates" />
					</LinkItem>
				) : null}
			</Fragment>
		);
	}, [isTemplateEditor, isCompanyHub, formatMessage]);

	const renderNCSSynchronyIndicator = useMemo(
		() => (
			<Query<UserContextQueryResults>
				/* @ts-expect-error -- http://go/cfe-use-relay-artifacts */
				query={UserContextQuery}
			>
				{({ data }) => {
					const atlassianStaff = isAtlassianStaff(data);

					if (!atlassianStaff) {
						return null;
					}

					if (isTemplateEditor) {
						return null;
					}

					const indicator = isNCSEditor ? (
						<FormattedMessage
							id="editor.help.dev-only-ncs-editor"
							defaultMessage="DEV ONLY: NCS editor"
							description="Dev Menu item indicator for NCS editor"
						/>
					) : (
						<FormattedMessage
							id="editor.help.dev-only-synchrony-editor"
							defaultMessage="DEV ONLY: Synchrony editor"
							description="Dev Menu item indicator for Synchrony editor"
						/>
					);

					return <ButtonItem>{indicator}</ButtonItem>;
				}}
			</Query>
		),
		[isNCSEditor, isTemplateEditor],
	);

	const renderFeedbackItem = useMemo(() => {
		const openGlobalFeedbackCollector = () => {
			setShouldShowGlobalFeedbackCollector?.(true);
		};
		return (
			<ButtonItem onClick={onItemClick(null, openGlobalFeedbackCollector)}>
				<FormattedMessage
					id="editor-help.fabric.help.center.feedback.item"
					defaultMessage="Feedback"
				/>
			</ButtonItem>
		);
	}, [onItemClick, setShouldShowGlobalFeedbackCollector]);

	return (
		<div
			css={styles.base}
			data-testid="help-menu-component"
			ref={setContainerRef}
			role="menu"
			aria-label={formatMessage({ id: 'editor-help.help.name', defaultMessage: 'Help menu' })}
			{...(expValEquals('confluence_editor_help_menu_keyboard_a11y', 'isEnabled', true) && {
				onKeyDown: handleKeyDown,
				tabIndex: -1,
			})}
		>
			<MenuGroup role="none">
				<Section>
					{renderNCSSynchronyIndicator}
					{renderDevTools}
					{renderHelpShortcutDropDownItem}
					{renderExternalLinkItems}
					{renderHelpCenter}
					{renderBlueprintTypeBasedDropdownItems}
					{renderFeedbackItem}
				</Section>
			</MenuGroup>
		</div>
	);
};

export const HelpMenu = LoadableLazy({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-HelpMenu" */ './HelpMenu')).HelpMenuComponent,
});
